/**
 * Asymmetric encryption utilities for secure document sharing
 * Uses Web Crypto API for RSA key generation and encryption
 */

export interface KeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

export interface SerializedKeyPair {
  publicKey: string;
  privateKey: string;
}

export interface EncryptedKeyData {
  encryptedKey: string;
  encryptedIv: string;
  recipientPublicKeyFingerprint: string;
}

/**
 * Generate RSA key pair for asymmetric encryption
 */
export const generateKeyPair = async (): Promise<KeyPair> => {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true, // extractable
      ['encrypt', 'decrypt']
    );

    return {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
    };
  } catch (error: any) {
    throw new Error(`Key generation failed: ${error.message}`);
  }
};

/**
 * Export key pair to serializable format
 */
export const exportKeyPair = async (keyPair: KeyPair): Promise<SerializedKeyPair> => {
  try {
    const publicKeyBuffer = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
    const privateKeyBuffer = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

    return {
      publicKey: arrayBufferToBase64(publicKeyBuffer),
      privateKey: arrayBufferToBase64(privateKeyBuffer),
    };
  } catch (error: any) {
    throw new Error(`Key export failed: ${error.message}`);
  }
};

/**
 * Import key pair from serialized format
 */
export const importKeyPair = async (serializedKeys: SerializedKeyPair): Promise<KeyPair> => {
  try {
    const publicKeyBuffer = base64ToArrayBuffer(serializedKeys.publicKey);
    const privateKeyBuffer = base64ToArrayBuffer(serializedKeys.privateKey);

    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      publicKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt']
    );

    const privateKey = await window.crypto.subtle.importKey(
      'pkcs8',
      privateKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['decrypt']
    );

    return { publicKey, privateKey };
  } catch (error: any) {
    throw new Error(`Key import failed: ${error.message}`);
  }
};

/**
 * Import public key from base64 string
 */
export const importPublicKey = async (publicKeyBase64: string): Promise<CryptoKey> => {
  try {
    const publicKeyBuffer = base64ToArrayBuffer(publicKeyBase64);
    
    return await window.crypto.subtle.importKey(
      'spki',
      publicKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt']
    );
  } catch (error: any) {
    throw new Error(`Public key import failed: ${error.message}`);
  }
};

/**
 * Encrypt document keys using recipient's public key
 */
export const encryptKeysForRecipient = async (
  documentKey: string,
  documentIv: string,
  recipientPublicKey: CryptoKey
): Promise<EncryptedKeyData> => {
  try {
    // Create fingerprint of recipient's public key for verification
    const publicKeyBuffer = await window.crypto.subtle.exportKey('spki', recipientPublicKey);
    const fingerprint = await createKeyFingerprint(publicKeyBuffer);

    // Combine key and IV into a single payload
    const keyData = JSON.stringify({ key: documentKey, iv: documentIv });
    const keyDataBuffer = new TextEncoder().encode(keyData);

    // Encrypt the key data
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      recipientPublicKey,
      keyDataBuffer
    );

    return {
      encryptedKey: arrayBufferToBase64(encryptedBuffer),
      encryptedIv: '', // Not used in RSA encryption, kept for compatibility
      recipientPublicKeyFingerprint: fingerprint,
    };
  } catch (error: any) {
    throw new Error(`Key encryption failed: ${error.message}`);
  }
};

/**
 * Decrypt document keys using user's private key
 */
export const decryptKeysFromSender = async (
  encryptedKeyData: EncryptedKeyData,
  userPrivateKey: CryptoKey
): Promise<{ key: string; iv: string }> => {
  try {
    const encryptedBuffer = base64ToArrayBuffer(encryptedKeyData.encryptedKey);

    // Decrypt the key data
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      userPrivateKey,
      encryptedBuffer
    );

    const keyDataString = new TextDecoder().decode(decryptedBuffer);
    const keyData = JSON.parse(keyDataString);

    return {
      key: keyData.key,
      iv: keyData.iv,
    };
  } catch (error: any) {
    throw new Error(`Key decryption failed: ${error.message}`);
  }
};

/**
 * Create a fingerprint of a public key for verification
 */
export const createKeyFingerprint = async (publicKeyBuffer: ArrayBuffer): Promise<string> => {
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', publicKeyBuffer);
  return arrayBufferToBase64(hashBuffer).substring(0, 16); // First 16 chars as fingerprint
};

/**
 * Store user's key pair in localStorage
 */
export const storeUserKeyPair = async (keyPair: KeyPair): Promise<void> => {
  try {
    const serializedKeys = await exportKeyPair(keyPair);
    localStorage.setItem('aegis_user_keypair', JSON.stringify(serializedKeys));
  } catch (error: any) {
    throw new Error(`Failed to store key pair: ${error.message}`);
  }
};

/**
 * Retrieve user's key pair from localStorage
 */
export const getUserKeyPair = async (): Promise<KeyPair | null> => {
  try {
    const stored = localStorage.getItem('aegis_user_keypair');
    if (!stored) return null;

    const serializedKeys: SerializedKeyPair = JSON.parse(stored);
    return await importKeyPair(serializedKeys);
  } catch (error: any) {
    console.error('Failed to retrieve key pair:', error);
    return null;
  }
};

/**
 * Get user's public key as base64 string
 */
export const getUserPublicKeyBase64 = async (): Promise<string | null> => {
  try {
    const keyPair = await getUserKeyPair();
    if (!keyPair) return null;

    const publicKeyBuffer = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
    return arrayBufferToBase64(publicKeyBuffer);
  } catch (error: any) {
    console.error('Failed to get public key:', error);
    return null;
  }
};

/**
 * Initialize user's key pair if it doesn't exist
 */
export const initializeUserKeyPair = async (): Promise<KeyPair> => {
  try {
    let keyPair = await getUserKeyPair();
    
    if (!keyPair) {
      keyPair = await generateKeyPair();
      await storeUserKeyPair(keyPair);
    }

    return keyPair;
  } catch (error: any) {
    throw new Error(`Failed to initialize key pair: ${error.message}`);
  }
};

/**
 * Utility functions
 */
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate Algorand wallet address format
 */
export const isValidWalletAddress = (address: string): boolean => {
  // Algorand addresses are 58 characters long and use base32 encoding
  return /^[A-Z2-7]{58}$/.test(address);
};